import {useRef, useState} from 'react';
import {ISpot} from '../../../../models/cleanUp';
import {
    addImageSpot,
    deleteImageSpot,
    uploadSpotImage,
    replaceSpotImage,
} from '../../../../api/cleanUp';
import s from '../index.module.scss';

interface Props {
    cleanUpId: string;
    initialSpots: ISpot[];
}

type UploadingKey = {spotId: string; type: 'before' | 'after'} | null;

interface SpotCardProps {
    spot: ISpot;
    index: number;
    uploading: UploadingKey;
    onDelete: (spotId: string) => void;
    onFileChange: (
        spotId: string,
        type: 'before' | 'after',
        file: File,
        existingImageId: string | null
    ) => void;
}

function SpotCard({spot, index, uploading, onDelete, onFileChange}: SpotCardProps) {
    const beforeRef = useRef<HTMLInputElement>(null);
    const afterRef = useRef<HTMLInputElement>(null);

    const handleSlotClick = (type: 'before' | 'after') => {
        if (type === 'before') beforeRef.current?.click();
        else afterRef.current?.click();
    };

    const handleInput = (type: 'before' | 'after', e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const existingId = spot[type].image_id;
        onFileChange(spot.id, type, file, existingId);
        e.target.value = '';
    };

    return (
        <div className={s.spotCard}>
            <div className={s.spotHeader}>
                <span className={s.spotLabel}>Spot {index + 1}</span>
                <button
                    type="button"
                    className={s.spotDeleteBtn}
                    onClick={() => onDelete(spot.id)}
                    title="Delete spot"
                >
                    ✕
                </button>
            </div>

            <div className={s.spotImages}>
                {(['before', 'after'] as const).map((type) => {
                    const item = spot[type];
                    const isLoading = uploading?.spotId === spot.id && uploading?.type === type;

                    return (
                        <div key={type} className={s.spotSlotWrapper}>
                            <div
                                className={`${s.spotSlot}${item.image_url ? ` ${s.spotSlotFilled}` : ''}`}
                                onClick={() => !isLoading && handleSlotClick(type)}
                            >
                                {isLoading ? (
                                    <div className={s.spotUploading}>Uploading…</div>
                                ) : item.image_url ? (
                                    <>
                                        <img
                                            src={item.image_url}
                                            alt={type}
                                            className={s.spotImage}
                                        />
                                        <div className={s.spotReplaceOverlay}>Replace</div>
                                    </>
                                ) : (
                                    <div className={s.spotPlaceholder}>
                                        <svg
                                            width="36"
                                            height="36"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        >
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <span className={s.spotTypeLabel}>{type}</span>
                            <input
                                ref={type === 'before' ? beforeRef : afterRef}
                                type="file"
                                accept="image/jpeg,image/png"
                                style={{display: 'none'}}
                                onChange={(e) => handleInput(type, e)}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function StepGallery({cleanUpId, initialSpots}: Props) {
    const [spots, setSpots] = useState<ISpot[]>(initialSpots);
    const [uploading, setUploading] = useState<UploadingKey>(null);
    const [error, setError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleAddSpot = async () => {
        setError(null);
        try {
            const updated = await addImageSpot(cleanUpId);
            setSpots(updated);
        } catch {
            setError('Failed to add spot.');
        }
    };

    const handleDeleteSpot = (spotId: string) => {
        setError(null);
        setDeleteTarget(spotId);
    };

    const confirmDeleteSpot = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const updated = await deleteImageSpot(cleanUpId, deleteTarget);
            setSpots(updated);
            setDeleteTarget(null);
        } catch (e: unknown) {
            setError(typeof e === 'string' ? e : 'Failed to delete spot.');
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    const handleFileChange = async (
        spotId: string,
        type: 'before' | 'after',
        file: File,
        existingImageId: string | null
    ) => {
        setError(null);
        setUploading({spotId, type});
        try {
            const updated = existingImageId
                ? await replaceSpotImage(cleanUpId, spotId, type, file)
                : await uploadSpotImage(cleanUpId, spotId, type, file);
            setSpots(updated);
        } catch (e: unknown) {
            const msg = (e as {response?: {data?: {message?: string | string[]}}})?.response?.data
                ?.message;
            setError(Array.isArray(msg) ? msg[0] : msg || 'Upload failed. Check file type and size (max 5 MB, JPEG/PNG only).');
        } finally {
            setUploading(null);
        }
    };

    return (
        <>
            <div className={s.stepBody}>
                {error && <div className={s.errorBanner}>{error}</div>}

                {spots.length === 0 && (
                    <p className={s.fieldHint}>
                        No spots yet. Add a spot to upload before/after photos.
                    </p>
                )}

                {spots.map((spot, index) => (
                    <SpotCard
                        key={spot.id}
                        spot={spot}
                        index={index}
                        uploading={uploading}
                        onDelete={handleDeleteSpot}
                        onFileChange={handleFileChange}
                    />
                ))}

                <button
                    type="button"
                    onClick={handleAddSpot}
                    className={s.addSpotButton}
                    disabled={uploading !== null}
                >
                    + Add Spot
                </button>
            </div>

            {deleteTarget && (
                <div className={s.modalOverlay}>
                    <div className={s.modal}>
                        <h3 className={s.modalTitle}>Delete Spot</h3>
                        <p className={s.modalBody}>
                            Are you sure you want to delete this spot and all its images? This
                            action cannot be undone.
                        </p>
                        <div className={s.modalActions}>
                            <button
                                className={s.modalCancel}
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                className={s.modalConfirm}
                                onClick={confirmDeleteSpot}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
